import { AnnouncementAudienceType } from '@/app/enums/AnnouncementAudienceType';
import { type AnnouncementReceiptStatus } from '@/app/enums/AnnouncementReceiptStatus';
import { type AnnouncementStatus } from '@/app/enums/AnnouncementStatus';
import { AnnouncementType } from '@/app/enums/AnnouncementType';
import { type AnnouncementDto } from '@/app/models/AnnouncementDto';
import { type AnnouncementPayload } from '@/app/models/AnnouncementPayload';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { type ContactDto } from '@/app/models/Contact';
import { type EnumType } from '@/app/models/EnumType';
import { type AnnouncementMediaNode, type AnnouncementMediaSize } from '@/app/modules/announcements/core/_models';
import { type AttachmentUploadProgress } from '@/app/modules/attachments/core/_models';
import { type Editor } from '@tiptap/react';

/** Datas no formato do `datetime-local`; o público guarda só os ids, como o backend. */
export type AnnouncementFormValues = {
  title: string;
  body: string;
  attachments: AttachmentDto[];
  type: AnnouncementType;
  audienceType: AnnouncementAudienceType;
  userRoleIds: string[];
  userIds: string[];
  startsNow: boolean;
  startDate: string;
  endDate: string;
  emailEnabled: boolean;
};

export const initialAnnouncementFormValuesEmpty: AnnouncementFormValues = {
  title: '',
  body: '',
  attachments: [],
  type: AnnouncementType.MANDATORY,
  audienceType: AnnouncementAudienceType.ALL,
  userRoleIds: [],
  userIds: [],
  startsNow: true,
  startDate: '',
  endDate: '',
  emailEnabled: false,
};

export const getAnnouncementFormValues = (values: Partial<AnnouncementFormValues>): AnnouncementFormValues => ({
  title: values.title ?? initialAnnouncementFormValuesEmpty.title,
  body: values.body ?? initialAnnouncementFormValuesEmpty.body,
  type: values.type ?? initialAnnouncementFormValuesEmpty.type,
  audienceType: values.audienceType ?? initialAnnouncementFormValuesEmpty.audienceType,
  startsNow: values.startsNow ?? initialAnnouncementFormValuesEmpty.startsNow,
  startDate: values.startDate ?? initialAnnouncementFormValuesEmpty.startDate,
  endDate: values.endDate ?? initialAnnouncementFormValuesEmpty.endDate,
  emailEnabled: values.emailEnabled ?? initialAnnouncementFormValuesEmpty.emailEnabled,
  userRoleIds: [...(values.userRoleIds ?? initialAnnouncementFormValuesEmpty.userRoleIds)],
  userIds: [...(values.userIds ?? initialAnnouncementFormValuesEmpty.userIds)],
  attachments: [...(values.attachments ?? initialAnnouncementFormValuesEmpty.attachments)],
});

/** O que o corpo tem de conteúdo: texto escrito, vídeo do YouTube e as imagens e vídeos que aparecem nele. */
export type AnnouncementBodyContent = {
  hasText: boolean;
  hasYoutubeVideo: boolean;
  mediaUrls: string[];
  hasPendingMedia: boolean;
};

/** Um formato aceito no envio: o nó que o mostra, as extensões que combinam e o tamanho máximo. */
export type AnnouncementMediaFormat = {
  type: AnnouncementMediaNode;
  extensions: string[];
  maxBytes: number;
};

export type AnnouncementMediaFile = {
  file: File;
  type: AnnouncementMediaNode;
};

/** Uma cor da paleta do editor: o nome que a pessoa vê e o valor gravado no HTML. */
export type AnnouncementColorOption = {
  label: string;
  value: string;
};

/** Um botão da toolbar do editor: o que ele mostra, quando está ligado e o que faz. */
export type AnnouncementToolbarAction = {
  label: string;
  icon: string;
  isActive: (editor: Editor) => boolean;
  run: (editor: Editor) => void;
  /** Sem a função, o botão fica sempre disponível (fora o editor travado). */
  isDisabled?: (editor: Editor) => boolean;
};

/** Uma largura da mídia selecionada: o nome do botão, o texto que ele mostra e o valor gravado no HTML. */
export type AnnouncementMediaSizeOption = {
  label: string;
  text: string;
  size: AnnouncementMediaSize;
};

export enum AnnouncementMediaUploadStatus {
  QUEUED = 'QUEUED',
  UPLOADING = 'UPLOADING',
  FAILED = 'FAILED',
}

export type AnnouncementMediaUploadEntry = {
  id: string;
  filename: string;
  status: AnnouncementMediaUploadStatus;
  progress: number;
  error?: string;
};

/** Uma imagem ou vídeo que acabou de entrar na fila: o editor a insere já, com a origem provisória do envio. */
export type AnnouncementMediaSlot = {
  type: AnnouncementMediaNode;
  src: string;
  alt: string;
};

export type AnnouncementMediaEnqueue = (files: File[]) => AnnouncementMediaSlot[];

export type AnnouncementMediaUploadOptions = {
  body: string;
  onBodyChange: (body: string) => void;
  onMediaUploaded: (attachment: AttachmentDto) => void;
  active: boolean;
  sourceId?: string;
  initialValues: AnnouncementFormValues;
};

export type AnnouncementMediaUploads = {
  entries: AnnouncementMediaUploadEntry[];
  enqueue: AnnouncementMediaEnqueue;
  retry: (id: string) => void;
  remove: (id: string) => void;
  reset: () => void;
  isBlocked: boolean;
};

export type AnnouncementMediaUploadFile = {
  file: File;
  session: number;
};

export type AnnouncementMediaUploadVariables = {
  file: File;
  signal: AbortSignal;
  onUploadProgress: AttachmentUploadProgress;
};

export type AnnouncementValidityFormValues = {
  startDate: string;
  endDate: string;
};

export enum AnnouncementsTab {
  RECEIVED = 'RECEIVED',
  MANAGED = 'MANAGED',
}

/** O filtro das listas de recebidos e criados; os `*_label` aparecem nos chips do filtro aplicado. */
export type AnnouncementListFilterValues = {
  status?: AnnouncementStatus;
  status_label?: string;
  type?: AnnouncementType;
  type_label?: string;
};

export type AnnouncementStatusOption = {
  value: AnnouncementStatus;
  label: string;
};

export type AnnouncementTypeOption = {
  value: AnnouncementType;
  label: string;
};

/** Título e tipo, como o autor e quem recebe veem a linha da listagem. */
export type AnnouncementSummary = {
  title: string;
  type: EnumType<AnnouncementType>;
};

export type ReceiptStatusOption = {
  value: AnnouncementReceiptStatus;
  label: string;
};

/** O filtro de situação da tabela do relatório. */
export type ReceiptStatusFilterValues = {
  status?: AnnouncementReceiptStatus;
};

export enum AnnouncementConfirmAction {
  PUBLISH = 'PUBLISH',
  CLOSE = 'CLOSE',
  REMOVE = 'REMOVE',
}

export type AnnouncementActionRequest = (id: string) => Promise<unknown>;

/** A cor do botão de confirmar, na classe `btn-*` do Bootstrap. */
export enum ConfirmActionVariant {
  PRIMARY = 'primary',
  DANGER = 'danger',
}

/** O que muda entre publicar, encerrar e excluir: o texto da confirmação e a chamada. */
export type ConfirmActionConfig = {
  title: string;
  question: (announcementTitle: string) => string;
  confirmLabel: string;
  variant: ConfirmActionVariant;
  request: AnnouncementActionRequest;
  successMessage: string;
};

export type PendingConfirmation = {
  action: AnnouncementConfirmAction;
  announcement: AnnouncementDto;
};

export type AnnouncementActionVariables = {
  id: string;
  request: AnnouncementActionRequest;
  successMessage: string;
};

export type ManagedAnnouncementHandlers = {
  onView: (announcement: AnnouncementDto) => void;
  onEdit: (announcement: AnnouncementDto) => void;
  onPublish: (announcement: AnnouncementDto) => void;
  onDuplicate: (announcement: AnnouncementDto) => void;
  onRemove: (announcement: AnnouncementDto) => void;
  onReport: (announcementId: string) => void;
  onValidity: (announcement: AnnouncementDto) => void;
  onClose: (announcement: AnnouncementDto) => void;
};

export type AudienceContactsResult = {
  contacts: ContactDto[];
  isTruncated: boolean;
};

/** O que o formulário manda salvar: os valores e se publica junto. */
export type AnnouncementSaveVariables = {
  values: AnnouncementFormValues;
  shouldPublish: boolean;
};

/** O que muda entre criar (ou duplicar) e editar: de onde vêm os valores e para onde vão. */
export type AnnouncementFormOptions = {
  source?: AnnouncementDto;
  toFormValues: (announcement: AnnouncementDto) => AnnouncementFormValues;
  save: (payload: AnnouncementPayload, shouldPublish: boolean) => Promise<unknown>;
  draftSuccessMessage: string;
  onSaved: () => void;
};
